import React, { useState, useEffect, useCallback } from 'react';
import { crewAPI } from '../utils/api';
import { Users, X, ArrowRight, Merge } from 'lucide-react';
import { toast } from 'sonner';

const reasonLabel = { name: 'Same name', email: 'Same email', phone: 'Same phone' };

const DuplicateCrewPanel = ({ onMerged }) => {
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null); // group being merged
  const [primary, setPrimary] = useState(null);
  const [selected, setSelected] = useState({}); // crew_id -> bool (to merge)
  const [busy, setBusy] = useState(false);

  const fetchDuplicates = useCallback(async () => {
    try {
      const r = await crewAPI.duplicates();
      setGroups(r.data || []);
    } catch (e) {
      // ambient widget, stay silent
    }
  }, []);

  useEffect(() => { fetchDuplicates(); }, [fetchDuplicates]);

  const openMerge = (group) => {
    const primaryId = group.members[0]?.crew_id;
    setActive(group);
    setPrimary(primaryId);
    const sel = {};
    group.members.forEach(m => { sel[m.crew_id] = m.crew_id !== primaryId; });
    setSelected(sel);
  };

  const setPrimaryAndSync = (crewId) => {
    setPrimary(crewId);
    setActive(a => {
      const sel = {};
      a.members.forEach(m => { sel[m.crew_id] = m.crew_id !== crewId; });
      setSelected(sel);
      return a;
    });
  };

  const handleMerge = async () => {
    const duplicate_crew_ids = Object.keys(selected).filter(id => selected[id] && id !== primary);
    if (duplicate_crew_ids.length === 0) { toast.error('Select at least one record to merge'); return; }
    const primaryMember = active.members.find(m => m.crew_id === primary);
    if (!window.confirm(
      `Merge ${duplicate_crew_ids.length} record(s) into "${primaryMember?.full_name}"?\n\n` +
      `All their assignments, blocked dates and workflow uploads will move to this record. ` +
      `The duplicate record(s) will be permanently removed. This cannot be undone.`
    )) return;
    setBusy(true);
    try {
      const r = await crewAPI.merge({ primary_crew_id: primary, duplicate_crew_ids });
      toast.success(`Merged ${r.data.merged_count} record(s) · ${r.data.reassigned_assignments} assignments moved`);
      setActive(null);
      await fetchDuplicates();
      if (onMerged) onMerged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to merge');
    } finally {
      setBusy(false);
    }
  };

  if (groups.length === 0) return null;

  return (
    <>
      <div className="border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-600 p-4 mb-4 sm:mb-6" data-testid="duplicate-crew-alert">
        <div className="flex items-start gap-3">
          <Users size={20} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                {groups.length} potential duplicate crew group{groups.length !== 1 ? 's' : ''} detected
              </p>
              <button
                onClick={() => setOpen(v => !v)}
                className="text-xs text-amber-800 dark:text-amber-300 hover:text-amber-900 dark:text-amber-300 underline underline-offset-2"
                data-testid="duplicate-crew-toggle"
              >
                {open ? 'Hide' : 'Review'}
              </button>
            </div>
            {open && (
              <div className="mt-3 space-y-3">
                {groups.map((g, gi) => (
                  <div key={gi} className="p-3 bg-card/70 dark:bg-amber-950/40 rounded" data-testid={`duplicate-group-${gi}`}>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {g.match_reasons.map(r => (
                        <span key={r} className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 bg-amber-200 dark:bg-amber-800 text-amber-900 dark:text-amber-100 rounded">
                          {reasonLabel[r] || r}
                        </span>
                      ))}
                    </div>
                    <div className="space-y-1">
                      {g.members.map(m => (
                        <div key={m.crew_id} className="text-xs text-amber-900 dark:text-amber-200 flex flex-wrap gap-x-2">
                          <span className="font-medium">{m.full_name}</span>
                          <span className="text-amber-700 dark:text-amber-400">· {m.email}</span>
                          <span className="text-amber-700 dark:text-amber-400">· {m.phone}</span>
                          <span className="text-amber-700 dark:text-amber-400">· {m.assignment_count} assignment{m.assignment_count !== 1 ? 's' : ''}</span>
                          {!m.is_active && <span className="text-amber-600 dark:text-amber-400">· archived</span>}
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => openMerge(g)}
                      className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-amber-600 text-white hover:bg-amber-700 rounded"
                      data-testid={`merge-group-btn-${gi}`}
                    >
                      <Merge size={12} /> Merge these
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {active && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="merge-crew-modal">
          <div className="bg-background w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 rounded">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-serif">Merge Crew Members</h2>
              <button onClick={() => setActive(null)} className="p-2 hover:bg-accent" data-testid="merge-modal-close"><X size={18} /></button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Choose the record to <strong>keep</strong> (primary). All history from the others will move to it, then the others are deleted.
            </p>
            <div className="space-y-2">
              {active.members.map(m => (
                <label
                  key={m.crew_id}
                  className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${primary === m.crew_id ? 'border-gold bg-gold/5' : 'border-border hover:border-gold/50'}`}
                  data-testid={`merge-primary-option-${m.crew_id}`}
                >
                  <input
                    type="radio"
                    name="primary"
                    checked={primary === m.crew_id}
                    onChange={() => setPrimaryAndSync(m.crew_id)}
                    className="mt-1"
                    data-testid={`merge-primary-radio-${m.crew_id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium flex items-center gap-2">
                      {m.full_name}
                      {primary === m.crew_id && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 bg-primary text-primary-foreground rounded">Keep</span>}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{m.email} · {m.phone}</p>
                    <p className="text-xs text-muted-foreground">{m.assignment_count} assignment{m.assignment_count !== 1 ? 's' : ''}{m.has_login ? ' · has login' : ''}{!m.is_active ? ' · archived' : ''}</p>
                  </div>
                </label>
              ))}
            </div>
            <div className="mt-4 p-3 bg-muted/40 text-xs text-muted-foreground rounded flex items-center gap-2">
              <ArrowRight size={14} className="text-gold flex-shrink-0" />
              {Object.keys(selected).filter(id => selected[id] && id !== primary).length} record(s) will be merged into the primary and deleted.
            </div>
            <div className="flex gap-3 pt-4">
              <button
                onClick={handleMerge}
                disabled={busy}
                className="flex items-center gap-2 bg-amber-600 text-white px-5 py-2.5 hover:bg-amber-700 disabled:opacity-50 text-sm"
                data-testid="confirm-merge-btn"
              >
                <Merge size={14} /> {busy ? 'Merging...' : 'Merge Now'}
              </button>
              <button onClick={() => setActive(null)} className="border border-border px-5 py-2.5 hover:bg-accent text-sm" data-testid="cancel-merge-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DuplicateCrewPanel;
